import Barcode from 'react-barcode';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Printer, Copy, Check, Barcode as BarcodeIcon } from 'lucide-react';
import { useState } from 'react';

interface BarcodeInfo {
  id: number;
  barcode: string;
  type: string;
  isPrimary: boolean;
  isExternal: boolean;
}

interface BarcodeDisplayProps {
  barcodes: BarcodeInfo[];
  skuCode?: string;
  itemName?: string;
  showSkuBarcode?: boolean;
}

// Map backend barcode types to react-barcode format strings
const BARCODE_FORMAT_MAP: Record<string, string> = {
  'CODE128': 'CODE128',
  'EAN13': 'EAN13',
  'EAN8': 'EAN8',
  'UPC': 'UPC',
  'CODE39': 'CODE39',
  'INTERNAL': 'CODE128',
};

export function BarcodeDisplay({ barcodes, skuCode, itemName }: BarcodeDisplayProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handlePrint = (barcode: string, format: string, sku?: string) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
  <title>Código de Barras - ${barcode}</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
  <style>
    body {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      margin: 0;
      font-family: sans-serif;
    }
    .label {
      text-align: center;
      margin-bottom: 10px;
      font-size: 12px;
      color: #666;
    }
    .sku {
      text-align: center;
      margin-top: 8px;
      font-size: 11px;
      color: #888;
      font-family: monospace;
    }
    @media print {
      body { padding: 20mm; }
    }
  </style>
</head>
<body>
  ${itemName ? `<div class="label">${itemName}</div>` : ''}
  <svg id="barcode"></svg>
  ${sku ? `<div class="sku">SKU: ${sku}</div>` : ''}
  <script>
    JsBarcode("#barcode", "${barcode}", {
      format: "${format}",
      width: 2,
      height: 80,
      displayValue: true,
      fontSize: 14,
      margin: 10
    });
    window.onload = function() {
      setTimeout(function() {
        window.print();
        window.close();
      }, 250);
    };
  <\/script>
</body>
</html>`);
    doc.close();
  };

  // Only use inventory barcodes (no SKU barcode generation)
  if (barcodes.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4">
        <BarcodeIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>Nenhum código de barras cadastrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {barcodes.map((bc) => {
        const format = BARCODE_FORMAT_MAP[bc.type] || 'CODE128';
        const bcId = String(bc.id);

        return (
          <Card key={bcId} className="overflow-hidden">
            <CardHeader className="py-2 px-4 bg-muted/50">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarcodeIcon className="h-4 w-4" />
                  Código de Inventário
                </div>
                <span className="text-xs text-muted-foreground font-mono">
                  {bc.type}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg">
                  <Barcode
                    value={bc.barcode}
                    format={format as any}
                    width={1.5}
                    height={60}
                    fontSize={12}
                    margin={5}
                    displayValue={true}
                  />
                  {skuCode && (
                    <div className="text-center mt-2 text-xs text-muted-foreground font-mono">
                      SKU: {skuCode}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(bc.barcode, bcId)}
                    className="gap-1"
                  >
                    {copiedId === bcId ? (
                      <>
                        <Check className="h-3 w-3" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Copiar
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrint(bc.barcode, format, skuCode)}
                    className="gap-1"
                  >
                    <Printer className="h-3 w-3" />
                    Imprimir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
